import {
	useCreateDocumentDraftMutation,
	ModifyInvoiceItemInput,
	DocumentType,
	CreditNoteKind,
	DocumentRole,
} from "@repo/server-api";
import {
	ResultFromData,
	ResultFromError,
	ResultFromException,
	useNotificationGraphql,
} from "@repo/shared/common";
import { useTranslation } from "react-i18next";

import type { CreditNote } from "../types/CommercialDocumentTypes";
import type { GraphqlQueryResult } from "@repo/shared/common";
import { InvoiceItem } from "../types";

type UseCreateCreditNoteOptions = {
	onSaved: (id: string) => void;
};

type UseCreateCreditNoteResult = {
	save: (creditNote: CreditNote) => void;
	loading: boolean;
};

export function useCreateCreditNoteHook(
	options: UseCreateCreditNoteOptions,
): UseCreateCreditNoteResult {
	const { t } = useTranslation();

	const [createDocumentDraftMutation] = useCreateDocumentDraftMutation();

	const createCreditNote = async (creditNote: CreditNote): Promise<GraphqlQueryResult<string>> => {
		const result = await createDocumentDraftMutation({
			variables: {
				documentType: DocumentType.CreditNote,
				creditNoteKind: creditNote.kind ?? CreditNoteKind.CustomerCreditMemo,
				documentRole: creditNote.documentRole ?? DocumentRole.Sales,
				settlesInvoiceId: creditNote.settlesInvoiceId ?? null,
				clientContactPersonId: creditNote.contactPerson?.id || null,
				clientId: creditNote.client?.id || null,
				concludingText: creditNote.footerNote,
				contractorId: creditNote.contractor?.id || null,
				introductoryText: creditNote.headerNote || "",
				items: creditNote.items.map((item: InvoiceItem, index: number) => ({
					description: item.description,
					discountPercent: item.discount ?? 0,
					name: item.name,
					quantity: item.quantity,
					tax: item.tax,
					taxCode: item.taxCode,
					unit: item.unit,
					unitPriceNet: item.unitPrice,
					positionNo: index,
				})) as ModifyInvoiceItemInput[],
				notes: creditNote.notes || "",
				paymentDeadlineInDays: creditNote.paymentDeadlineDays,
				projectId: creditNote.project?.id || null,
				isProforma: creditNote.isProforma || false,
			},
		});

		if (
			result.data?.createDocumentDraft.errors &&
			result.data?.createDocumentDraft.errors.length > 0
		) {
			return ResultFromError<string>(result.data.createDocumentDraft.errors);
		}
		if (result.errors) return ResultFromException<string>(result.errors);

		const doc = result.data?.createDocumentDraft.commercialDocument;
		const creditNoteId = doc?.__typename === "CreditNote" ? doc.id : null;
		if (!creditNoteId) return ResultFromException<string>(t("no-credit-note-id-returned"));

		return ResultFromData<string>(creditNoteId);
	};

	const { save, loading } = useNotificationGraphql<CreditNote, string>({
		onComplete: (id) => {
			options.onSaved(id);
		},

		graphqlCall: createCreditNote,

		successMessage: t("credit-note-created-successfully"),
	});

	return {
		save,
		loading,
	};
}
